import classNames from 'classnames';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { Link, useHistory } from 'react-router-dom';
import {
  Button,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ComposerSearchBar from '../composer-search-bar';
import { deleteOauthApplication, getOauthApplications } from '../../../services/oauth-application-service';
import { searchTypes } from '../../../services/search-service';
import { getSelectedAccount } from '../../../helpers/composer-helper';
import styles from '../composer.module.css';

const ApiKeys = () => {
  const history = useHistory();

  const account = useSelector((state) => getSelectedAccount(state));
  const { _id: accountId } = account;

  const [oAuthApplications, setOauthApplications] = useState([]);
  const [anchorElement, setAnchorElement] = useState(undefined);
  const [currentObject, setCurrentObject] = useState(undefined);
  const [refreshTimestamp, setRefreshTimestamp] = useState(null);

  const currentObjectId = currentObject ? currentObject._id : '';

  const handleDelete = (id) => {
    deleteOauthApplication(accountId, id).then(() => {
      setRefreshTimestamp(Date.now());
      //setToast({ status: 'success', message: 'Ad tag deleted' });
    });
  };

  return (
    <div className={styles.pageCenter}>
      <ComposerSearchBar
        fetchObjects={getOauthApplications}
        searchType={searchTypes.stream}
        serviceArgs={[accountId]}
        headerElements={
          <>
            <Button component={Link} className={classNames(styles.keyButton)} to="/api-keys/create">
              Create API Key
            </Button>
          </>
        }
        emptyState={
          <div className={classNames(styles.card, styles.flex, styles.justifyCenter, styles.lightBG)}>
            <div className={classNames(styles.flex, styles.flexColumn)}>
              <div
                className={classNames(
                  styles.p2,
                  styles.whiteBG,
                  styles.rounded,
                  styles.flex,
                  styles.purple,
                  styles.border
                )}
              >
                <KeyOutlinedIcon style={{ width: 28, height: 28 }} />
              </div>
              <div className={classNames(styles.center, styles.mb)}>
                No API keys
                <div className={classNames(styles.font14, styles.light)} style={{ maxWidth: 230 }}>
                  Create an api key to leverage our API.
                </div>
              </div>
              <div>
                <Button
                  component={Link}
                  className={classNames(styles.outlineKeyButton, styles.flex, styles.mb)}
                  to="/api-keys/create"
                  style={{ minWidth: 220 }}
                >
                  Create an API key
                  <div className={styles.f1} />
                  <ArrowForwardIcon fontSize="small" />
                </Button>
                <Button className={classNames(styles.outlineButton, styles.flex)} style={{ minWidth: 220 }}>
                  View guides
                  <div className={styles.f1} />
                  <ArrowForwardIcon fontSize="small" />
                </Button>
              </div>
            </div>
          </div>
        }
        onResultsChanged={(results) => {
          setOauthApplications(results);
        }}
        refreshTimestamp={refreshTimestamp}
      />
      {oAuthApplications.length > 0 && (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="left">Name</TableCell>
                <TableCell align="left">Client ID</TableCell>
                <TableCell align="left">Scope</TableCell>
                <TableCell align="left">Created</TableCell>
                <TableCell align="left"> </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {oAuthApplications.map((oAuthApplication) => {
                return (
                  <TableRow
                    key={oAuthApplication._id}
                    onClick={(e) => {
                      e.preventDefault();
                      history.push(`/api-keys/${oAuthApplication._id}`);
                    }}
                    className={classNames(styles.pointer, { active: oAuthApplication._id === currentObjectId })}
                  >
                    <TableCell align="left">
                      <div className={styles.strong}>{oAuthApplication.name}</div>
                    </TableCell>
                    <TableCell align="left">
                      <div className={styles.strong}>{oAuthApplication.clientId}</div>
                    </TableCell>
                    <TableCell align="left" className={styles.light}>
                      <div className={classNames(styles.strong, styles.flex)}>
                        {oAuthApplication.scopes.map((scope) => {
                          return (
                            <div key={scope}>
                              <Chip className={styles.chip} label={scope.replace('external-', '')} />
                            </div>
                          );
                        })}
                      </div>
                    </TableCell>
                    <TableCell align="left" className={styles.light}>
                      {new Date(oAuthApplication.createdAt).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label="more"
                        size="small"
                        onClick={(event) => {
                          setAnchorElement(event.currentTarget);
                          setCurrentObject(oAuthApplication);
                          event.stopPropagation();
                        }}
                      >
                        <MoreHorizIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Menu
        open={Boolean(anchorElement)}
        onClose={() => {
          setAnchorElement(null);
          setCurrentObject(null);
        }}
        anchorEl={anchorElement}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            if (!currentObject) {
              return;
            }
            setAnchorElement(null);
            setCurrentObject(null);
            history.push(`/api-keys/${currentObjectId}/edit`);
          }}
        >
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            if (!currentObjectId) {
              return;
            }
            e.stopPropagation();
            if (window.confirm('Are you sure you want to delete?')) {
              handleDelete(currentObjectId);
            }
            setAnchorElement(null);
            setCurrentObject(null);
          }}
        >
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};

export default ApiKeys;
